import React from 'react';
import {
  Modal,
  Button,
  Spin,
  Space,
  Row,
  Col,
  Card,
  Statistic,
  List,
  Avatar,
  Tag,
  Typography
} from 'antd';
import { BarChartOutlined, CrownOutlined } from '@ant-design/icons';

const { Text } = Typography;

const StatisticsModal = ({
  visible,
  statistics,
  loading,
  onClose,
  formatCurrency
}) => {
  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BarChartOutlined style={{ color: '#722ed1' }} />
          <span>Auction Statistics</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>
      ]}
      width={window.innerWidth <= 576 ? '95%' : 900}
      centered
      style={{ top: window.innerWidth <= 576 ? 10 : undefined }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>Loading statistics...</div>
        </div>
      ) : statistics ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Overview Cards */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Lots"
                  value={statistics.lots.total}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Sold Lots"
                  value={statistics.lots.sold}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Bids"
                  value={statistics.bids.total}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Revenue"
                  value={formatCurrency(statistics.revenue.total)}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Detailed Statistics */}
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card title="Lot Statistics" size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  {['active', 'closed', 'cancelled', 'sold_percentage'].map((key) => (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }} key={key}>
                      <Text>{key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}:</Text>
                      <Text strong>
                        {key === 'sold_percentage' ? `${statistics.lots[key]}%` : statistics.lots[key]}
                      </Text>
                    </div>
                  ))}
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Bid Statistics" size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Unique Bidders:</Text>
                    <Text strong>{statistics.bids.unique_bidders}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Average Bid Amount:</Text>
                    <Text strong>{formatCurrency(statistics.bids.average_amount)}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Highest Bid:</Text>
                    <Text strong>{formatCurrency(statistics.bids.highest_bid)}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Avg Bids per Lot:</Text>
                    <Text strong>{statistics.bids.average_bids_per_lot}</Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Top Bidders */}
          {statistics.top_bidders?.length > 0 && (
            <Card title="Top Bidders" size="small">
              <List
                dataSource={statistics.top_bidders}
                renderItem={(bidder, index) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          style={{
                            background:
                              index < 3 ? ['#ffd700', '#c0c0c0', '#cd7f32'][index] : '#1890ff',
                            color: '#fff'
                          }}
                        >
                          {index < 3 ? <CrownOutlined /> : `#${index + 1}`}
                        </Avatar>
                      }
                      title={
                        <Space>
                          <Text strong>{bidder.name}</Text>
                          {index < 3 && (
                            <Tag color={index === 0 ? 'gold' : index === 1 ? 'default' : 'orange'}>
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                            </Tag>
                          )}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small">
                          <div>
                            <Text type="secondary">Total Bids: </Text>
                            <Text strong>{bidder.total_bids}</Text>
                            <Text type="secondary" style={{ marginLeft: '16px' }}>
                              Total Amount:{' '}
                            </Text>
                            <Text strong style={{ color: '#52c41a' }}>
                              {formatCurrency(bidder.total_amount)}
                            </Text>
                          </div>
                          <div>
                            <Text type="secondary">Highest Bid: </Text>
                            <Text strong>{formatCurrency(bidder.highest_bid)}</Text>
                            <Text type="secondary" style={{ marginLeft: '16px' }}>
                              Lots Bid On:{' '}
                            </Text>
                            <Text strong>{bidder.lots_bid_on}</Text>
                          </div>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          )}
        </Space>
      ) : null}
    </Modal>
  );
};

export default StatisticsModal;
